import React, { useEffect, useMemo, useState } from 'react';
import { SELECTORS } from '@oneaudi/stck-store';
import { fetchAnythingScs, useServicesContext, VehicleBasic } from '@oneaudi/vtp-shared';
import { useSelector } from 'react-redux';

import { TileContext, TileContextState } from '../../hooks/tileHook';
import ResultsBar from './ResultsBar';
import ZeroResultsPage from './ZeroResultsPage';
import Tiles from './Tiles';
import Tile from './Tile';
import { ContentService } from '@oneaudi/content-service';
import { LayoutContainer } from './Tiles.styles';
import { AudiStockcarsStoreServiceV1 } from '@oneaudi/stockcars-store-service';
import { getScsUrlPartsFromConfiguration } from '../../hooks/useScsUrlParts';
import { Logger } from '@feature-hub/core';

interface Props {}

const Favorites: React.FC<Props> = () => {
  const { getEnvironmentConfig } = useServicesContext();
  const logger: Logger = useServicesContext().getAdditionalService('s2:logger') ?? console;
  const storeService = useServicesContext().getAdditionalService(
    'audi-stockcars-store-service'
  ) as AudiStockcarsStoreServiceV1;
  const audiContentService = useServicesContext().getAdditionalService(
    'audi-content-service'
  ) as ContentService;
  const content = audiContentService.getContent();

  const [favoriteVehicles, setFavoriteVehicles] = useState<VehicleBasic[]>([]);
  const scsUrlParts = getScsUrlPartsFromConfiguration(
    content.fields.vtpConfiguration.fields,
    getEnvironmentConfig()!
  );
  const favoriteVehicleIDs: string[] = useSelector((state) =>
    SELECTORS.VEHICLES.getFavoriteVehicleIdsState(state)
  );
  const vehicleQueryParamValues = favoriteVehicleIDs.map((id: string) => `vehicle.${id}`);
  const tileContextValue: TileContextState = useMemo(() => {
    return {
      appContext: content.fields.tilesContext,
      contentConfiguration: content.fields,
    };
  }, []);

  useEffect(() => {
    if (!favoriteVehicleIDs) return;
    const getSortParam = () => {
      const storedSortValue = storeService.store
        ? SELECTORS.SORTING.getSortingStateRaw(storeService.store.state)
        : undefined;
      if (storedSortValue?.results) {
        return storedSortValue.results;
      }
      return content.fields.sortParams.options?.length
        ? content.fields.sortParams.options[0]
        : 'relevance';
    };

    const buildUrl = () => {
      const sortParam = encodeURIComponent(getSortParam());
      const filterParam = encodeURIComponent(vehicleQueryParamValues.join(','));
      const versionPath = scsUrlParts.versionsMapping?.filter || scsUrlParts.defaultVersion;
      return `${scsUrlParts.scsBaseUrl}${versionPath}/search/filter/${scsUrlParts.marketPath}?size=${favoriteVehicleIDs.length}&sort=${sortParam}&filter=${filterParam}`;
    };

    const fetchData = async () => {
      const url = buildUrl();
      try {
        const data = await fetchAnythingScs<{ vehicleBasic: VehicleBasic[] }>(
          url,
          scsUrlParts.apiKey
        );

        setFavoriteVehicles(data.vehicleBasic as VehicleBasic[]);
      } catch (error) {
        logger?.error(error);
      }
    };
    fetchData().then();
  }, [favoriteVehicleIDs]);

  return (
    <TileContext.Provider value={tileContextValue}>
      <LayoutContainer direction="column">
        <ResultsBar />
        {favoriteVehicles.length === 0 ? (
          <ZeroResultsPage context="favorites" />
        ) : (
          <>
            <Tiles>
              {favoriteVehicles.map((favoriteVehicle) => (
                <Tile
                  key={favoriteVehicle.id}
                  vehicle={favoriteVehicle}
                  data-testid={`favorite_${favoriteVehicle.id}`}
                />
              ))}
            </Tiles>
          </>
        )}
      </LayoutContainer>
    </TileContext.Provider>
  );
};

export default Favorites;
